import { lazy, Suspense, useMemo, useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load non-critical UI overlays to reduce initial bundle
const HelmetProvider = lazy(() => import("react-helmet-async").then(m => ({ default: m.HelmetProvider })));
const CookieBanner = lazy(() => import("@/components/CookieBanner").then(m => ({ default: m.CookieBanner })));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() => import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));
const OfflineBanner = lazy(() => import("@/components/OfflineBanner").then(m => ({ default: m.OfflineBanner })));
const PWAUpdatePrompt = lazy(() => import("@/components/PWAUpdatePrompt").then(m => ({ default: m.PWAUpdatePrompt })));

// Landing page loaded eagerly — it's the LCP-critical route
import LandingPage from "./pages/LandingPage";

// Auth-dependent routes in a single lazy chunk (defers ~25KB Supabase SDK)
const AuthenticatedApp = lazy(() => import("./components/AuthenticatedApp"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — avoid refetching on every mount
      gcTime: 10 * 60 * 1000, // 10 min garbage collection
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Lightweight landing route — renders LandingPage instantly without loading
 * AuthProvider or Supabase SDK (~25KB gzip). If a session token exists in
 * localStorage, redirects to /app where full auth will be verified.
 */
function LandingRoute() {
  const hasSession = useMemo(() => {
    try {
      const key = `sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`;
      const raw = localStorage.getItem(key);
      if (!raw) return false;
      // Check for token existence, not expiry — AuthProvider will handle
      // refresh via the SDK. Only skip redirect when there's truly no token.
      const parsed = JSON.parse(raw);
      return !!(parsed?.access_token || parsed?.refresh_token);
    } catch {
      return false;
    }
  }, []);

  // Preload AuthenticatedApp chunk before redirecting to avoid
  // "component suspended while responding to synchronous input" error
  const [ready, setReady] = useState(!hasSession);
  useEffect(() => {
    if (hasSession) {
      import("./components/AuthenticatedApp").then(() => setReady(true));
    }
  }, [hasSession]);

  if (hasSession && !ready) return null;
  if (hasSession) return <Navigate to="/app" replace />;
  return <LandingPage />;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Suspense fallback={null}>
          <HelmetProvider>
            <TooltipProvider>
              <Suspense fallback={null}>
                <Toaster />
                <Sonner />
                <OfflineBanner />
                <PWAUpdatePrompt />
              </Suspense>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Suspense fallback={null}>
                  <CookieBanner />
                </Suspense>
                <Routes>
                  {/* Landing: no AuthProvider, no Supabase — instant render */}
                  <Route path="/" element={<LandingRoute />} />
                  {/* Everything else: lazy-loads AuthProvider + Supabase SDK */}
                  <Route
                    path="/*"
                    element={
                      <Suspense
                        fallback={
                          <div className="flex items-center justify-center min-h-[60vh]">
                            <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        }
                      >
                        <AuthenticatedApp />
                      </Suspense>
                    }
                  />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </HelmetProvider>
        </Suspense>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
